import { Helmet } from "react-helmet-async";

interface BreadcrumbItem {
  name: string;
  href: string;
}

interface EventData {
  name: string;
  startDate: string;
  endDate: string;
  location: string;
  description: string;
  image?: string;
  organizer?: string;
  url?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  image?: string;
  article?: {
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
  };
  breadcrumbs?: BreadcrumbItem[];
  event?: EventData;
  /** Any additional JSON-LD objects to inject */
  jsonLd?: Record<string, unknown>[];
  /** Publish date for article OG tags */
  publishedTime?: string;
  /** Author name for article OG tags */
  author?: string;
  /** FAQ items for FAQ schema */
  faq?: FAQItem[];
  /** noindex / nofollow for pages that shouldn't be indexed */
  noIndex?: boolean;
  /** SEO override from database */
  seoOverride?: {
    meta_title?: string | null;
    meta_description?: string | null;
    og_image_url?: string | null;
  } | null;
}

const SITE_NAME = "NEXTPredict";
const BASE_URL = "https://nextpredict.io";
const DEFAULT_IMAGE = `${BASE_URL}/og-default.png`;

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: BASE_URL,
  logo: `${BASE_URL}/favicon.png`,
  sameAs: [
    "https://x.com/nextpredictio",
    "https://www.linkedin.com/company/nextpredict",
    "https://www.instagram.com/nextpredictio/",
  ],
};

const webSiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: BASE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE_URL}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

const SEO = ({
  title,
  description,
  canonical,
  type = "website",
  image,
  article,
  breadcrumbs,
  event,
  jsonLd,
  publishedTime,
  author,
  faq,
  noIndex,
  seoOverride,
}: SEOProps) => {
  const effectiveTitle = seoOverride?.meta_title || title;
  const effectiveDescription = seoOverride?.meta_description || description;
  const effectiveImage = seoOverride?.og_image_url || image || DEFAULT_IMAGE;
  const fullTitle = effectiveTitle === SITE_NAME ? effectiveTitle : `${effectiveTitle} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
  const ogImage = effectiveImage;

  const articleJsonLd = article
    ? {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: effectiveTitle,
        description: effectiveDescription,
        image: ogImage,
        author: { "@type": "Person", name: article.author || SITE_NAME },
        publisher: { "@type": "Organization", name: SITE_NAME, logo: { "@type": "ImageObject", url: `${BASE_URL}/favicon.png` } },
        datePublished: article.publishedTime,
        ...(article.modifiedTime ? { dateModified: article.modifiedTime } : {}),
        mainEntityOfPage: canonicalUrl,
        articleSection: article.section,
      }
    : null;

  const breadcrumbJsonLd = breadcrumbs && breadcrumbs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: `${BASE_URL}${item.href}`,
        })),
      }
    : null;

  const eventJsonLd = event
    ? {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.name,
        startDate: event.startDate,
        endDate: event.endDate,
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        eventStatus: "https://schema.org/EventScheduled",
        location: {
          "@type": "Place",
          name: event.location,
          address: { "@type": "PostalAddress", addressLocality: event.location },
        },
        image: event.image || ogImage,
        description: event.description,
        organizer: {
          "@type": "Organization",
          name: event.organizer || SITE_NAME,
          url: event.url || BASE_URL,
        },
      }
    : null;

  const faqJsonLd = faq && faq.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      }
    : null;

  const isArticle = type === "article";

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={effectiveDescription} />
      {author && <meta name="author" content={author} />}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={effectiveDescription} />
      <meta property="og:type" content={isArticle ? "article" : type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {isArticle && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {isArticle && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {isArticle && article?.section && (
        <meta property="article:section" content={article.section} />
      )}
      {isArticle && article?.author && (
        <meta property="article:author" content={article.author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@nextpredictio" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={effectiveDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* RSS autodiscovery */}
      <link rel="alternate" type="application/rss+xml" title={`${SITE_NAME} RSS Feed`} href="/rss.xml" />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webSiteJsonLd)}</script>
      {articleJsonLd && <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>}
      {breadcrumbJsonLd && <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>}
      {eventJsonLd && <script type="application/ld+json">{JSON.stringify(eventJsonLd)}</script>}
      {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      {jsonLd?.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
